// The document screen (design §14.2): the wordmark and the title above, the
// evaluation card at the width of the text, the editor with each comment under
// the last paragraph it is about, and everything the document can do in a pill
// floating clear of the bottom edge.
//
// Two review buttons, and they mean different things (Phase 4.5). The one in
// the dock is always the whole text, and it starts the evaluation beside the
// review so the author gets both without pressing twice; the one in the
// selection tray is always the passage they have chosen. Neither changes what
// the advisor is asked — only what it is asked about.
//
// Opening is offline-first. The cached copy and any unsent operations are
// read from this device and shown at once, so the document appears instantly
// and works with no connection. The server's version arrives afterwards and
// the unsent operations are replayed onto it, which is what keeps another
// device's work and this device's work both intact.

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Outlet, useMatch, useNavigate, useOutletContext, useParams } from "react-router-dom";

import { ApiError, type Me, type ServerDocument, api } from "../api/client";
import { CommentCard, when } from "../advisor/CommentCard";
import {
  type Comment,
  type Run,
  docScoped,
  open as openComments,
  selectionScope,
  trayIndex,
} from "../advisor/types";
import { useAdvisor } from "../advisor/useAdvisor";
import { rememberMe, searchAvailable } from "../storage/me";
import type { TextSelection } from "../editor/Editor";
import { useFind } from "../editor/useFind";
import { joinMarkdown } from "../markdown/split";
import { loadDocument, replay, saveDocument } from "../storage/cache";
import { DocumentStore } from "../store/documentStore";
import { type DocState, type Op, type ParagraphId, paragraphsOf } from "../store/ops";
import { type DocumentEvent, connectEvents } from "../sync/events";
import { Outbox, type SyncStatus, pendingFor } from "../sync/outbox";
import { BackDisc, IconButton, TopBar } from "../ui/TopBar";
import { ModelMenu } from "../ui/ModelMenu";
import { useLongPress } from "../ui/useLongPress";
import type { DocumentContext } from "./documentContext";
import { useKeyboardInset } from "./useKeyboardInset";

const SUCCESS_MS = 4000;

function toDocState(server: ServerDocument): DocState {
  const text: Record<string, string> = {};
  for (const [id, p] of Object.entries(server.paragraphs)) text[id] = p.text;
  return { title: server.title, order: server.order, text };
}

interface Session {
  store: DocumentStore;
  outbox: Outbox;
  close: () => void;
}

export function DocumentScreen() {
  // RequireSignIn puts the account on its outlet; this is the first screen to
  // read it, and the dock's model menu is why it needs it.
  const me = useOutletContext<Me>();
  const { id } = useParams<{ id: string }>();
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [selection, setSelection] = useState<ParagraphId[]>([]);
  // Bumped whenever the stream says the advisor has news, which is what makes
  // comments appear the moment a Review commits them.
  const [advisorBump, setAdvisorBump] = useState(0);
  const revRef = useRef(0);
  // Set up once the session exists, so History can put the document back from
  // inside the open screen rather than by reloading the page.
  const restoreRef = useRef<() => void>(() => {});
  // Stable, because it now travels inside a memoised context: an arrow rebuilt
  // on every render would rebuild the context with it and defeat the memo.
  const onRestored = useCallback(() => restoreRef.current(), []);
  const keyboardInset = useKeyboardInset();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let current: Session | null = null;
    let closeEvents: (() => void) | null = null;

    const say = (message: string) => {
      setNotice(message);
      window.setTimeout(() => setNotice(null), 4000);
    };

    (async () => {
      // 1. This device first, so the document is on screen before the network
      //    is consulted, and appears at all when there is no network.
      const [cached, queued] = await Promise.all([loadDocument(id), pendingFor(id)]);
      if (cancelled) return;

      const outbox = new Outbox(id, cached?.rev ?? 0, () => void reload());
      const store = new DocumentStore(
        cached ? replay(cached.doc, queued.map((q) => q.op)) : { title: "", order: [], text: {} },
        (op) => outbox.push(op),
      );
      outbox.adopt(queued);
      revRef.current = cached?.rev ?? 0;

      /** Take the server's picture again.
       *
       *  `replacing` is a restore (design §13): the document was deliberately
       *  put back to an older one, so unsent edits made against the newer text
       *  are dropped rather than replayed on top of it. */
      async function reload(replacing = false): Promise<void> {
        try {
          if (replacing) outbox.discard();
          const fresh = await api.get<ServerDocument>(`/api/documents/${id}`);
          revRef.current = fresh.rev;
          outbox.setRev(fresh.rev);
          store.reset(replay(toDocState(fresh), outbox.pendingOps()));
          void saveDocument(id!, toDocState(fresh), fresh.rev);
          if (!replacing) say("Reloaded from the server");
        } catch {
          setError("Lost the document. Reload the page.");
        }
      }
      restoreRef.current = () => void reload(true);

      const catchUp = async (): Promise<void> => {
        try {
          const result = await api.get<{
            rev: number;
            too_many: boolean;
            ops: { rev: number; client_id: string; op: Op }[];
          }>(`/api/documents/${id}/ops?since=${revRef.current}`);
          if (result.too_many) return void reload();
          for (const entry of result.ops) {
            if (entry.client_id !== outbox.clientId) store.applyRemote(entry.op);
          }
          revRef.current = result.rev;
          outbox.setRev(result.rev);
        } catch {
          void reload();
        }
      };

      const onEvent = (event: DocumentEvent) => {
        if (event.type === "advisor" || event.type === "run" || event.type === "run_step") {
          setAdvisorBump((n) => n + 1);
          return;
        }
        if (event.type === "restored") {
          // No operation produced this, so there is nothing to catch up from:
          // the document is taken again whole.
          void reload(true);
          setAdvisorBump((n) => n + 1);
          return;
        }
        if (event.type === "versions") {
          setAdvisorBump((n) => n + 1);
          return;
        }
        if (event.type === "open") {
          if (event.rev > revRef.current) void catchUp();
          return;
        }
        if (event.type === "ops") {
          if (event.client_id !== outbox.clientId) {
            try {
              for (const op of event.ops) store.applyRemote(op);
            } catch {
              void reload();
              return;
            }
          }
          revRef.current = Math.max(revRef.current, event.rev);
          outbox.setRev(event.rev);
        }
      };

      if (cached) setSession((current = { store, outbox, close: () => {} }));

      // 2. Then the server. Unsent operations go back on top of whatever it
      //    says, so nothing this device did is lost and nothing is applied twice.
      let opened = Boolean(cached);
      try {
        const server = await api.get<ServerDocument>(`/api/documents/${id}`);
        if (cancelled) return;
        revRef.current = server.rev;
        outbox.setRev(server.rev);
        store.reset(replay(toDocState(server), outbox.pendingOps()));
        void saveDocument(id, toDocState(server), server.rev);
        opened = true;
      } catch {
        if (cancelled) return;
        if (!opened) {
          setError("Could not open this document, and there is no copy on this device.");
          return;
        }
        say("Offline — showing the copy on this device");
      }

      closeEvents = connectEvents(id, onEvent, setLive);
      const flushOnHide = () => void outbox.flush(true);
      window.addEventListener("pagehide", flushOnHide);

      current = {
        store,
        outbox,
        close: () => {
          window.removeEventListener("pagehide", flushOnHide);
          closeEvents?.();
          void outbox.flush(true);
        },
      };
      if (!cancelled) setSession(current);
    })();

    return () => {
      cancelled = true;
      closeEvents?.();
      current?.close();
    };
  }, [id]);

  if (error) return <Shell>{<p className="error">{error}</p>}</Shell>;
  if (!session) return <Shell>{<p className="muted">Opening…</p>}</Shell>;

  return (
    <Open
      documentId={id!}
      outletMe={me}
      session={session}
      live={live}
      notice={notice}
      selection={selection}
      onSelectionChange={setSelection}
      keyboardInset={keyboardInset}
      revRef={revRef}
      advisorBump={advisorBump}
      onRestored={onRestored}
    />
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="screen">
      <TopBar />
      <main className="body">
        <BackDisc />
        {children}
      </main>
    </div>
  );
}

function Open({
  documentId,
  outletMe,
  session,
  live,
  notice,
  selection,
  onSelectionChange,
  keyboardInset,
  revRef,
  advisorBump,
  onRestored,
}: {
  documentId: string;
  outletMe: Me;
  session: Session;
  live: boolean;
  notice: string | null;
  selection: ParagraphId[];
  onSelectionChange: (ids: ParagraphId[]) => void;
  keyboardInset: number;
  revRef: React.RefObject<number>;
  advisorBump: number;
  onRestored: () => void;
}) {
  const { store, outbox } = session;
  const doc = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const status = useSyncExternalStore(outbox.subscribe, outbox.getSnapshot);
  const [shared, setShared] = useState<string | null>(null);
  const [finding, setFinding] = useState(false);
  const find = useFind(doc, finding);

  const navigate = useNavigate();
  const base = `/d/${documentId}`;
  // Which panel is open is a fact about the URL now, so the back disc, the
  // dock's lit buttons and the device's own back gesture cannot disagree.
  const inLog = useMatch({ path: "/d/:id/research/log", end: true }) !== null;
  const inResearch = useMatch({ path: "/d/:id/research", end: false }) !== null;
  const inHistory = useMatch({ path: "/d/:id/history", end: false }) !== null;

  const advisor = useAdvisor(documentId, advisorBump);
  const [openTray, setOpenTray] = useState<ParagraphId | null>(null);
  const [undo, setUndo] = useState<{ slug: string; timer: number } | null>(null);
  const [asking, setAsking] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(false);
  // The account, held rather than read once: the dock's model menu can change
  // which model is in use without leaving this screen, and the globe below
  // depends on the answer (D25).
  const [me, setMe] = useState<Me>(outletMe);
  const canSearch = useMemo(() => searchAvailable(me), [me]);
  // Null until the document says whether the web-search note is still owed
  // (design §9.2); `sheet` is that note, open and waiting for an answer.
  const [noticeShown, setNoticeShown] = useState<boolean | null>(null);
  const [sheet, setSheet] = useState(false);
  // What is selected inside a paragraph, if anything (design §6.2).
  const [textSelection, setTextSelection] = useState<TextSelection | null>(null);
  // Bumped to tell the editor to collapse the caret: clearing the selection
  // from the dock has already taken focus away from the text area.
  const [clearText, setClearText] = useState(0);
  // A paragraph a panel asked to be shown on its way out.
  const pendingScroll = useRef<ParagraphId | null>(null);
  const [modelMenu, setModelMenu] = useState(false);
  // Comments whose reassess just came back "addressed": shown as Success for
  // a moment before they leave (design §7.4).
  const [flashing, setFlashing] = useState<Map<string, Comment>>(new Map());
  const awaiting = useRef(new Set<string>());

  useEffect(() => {
    api
      .get<ServerDocument>(`/api/documents/${documentId}`)
      .then((d) => {
        setSearchEnabled(d.search_enabled);
        setNoticeShown(d.search_notice_shown_at !== null);
      })
      .catch(() => {});
  }, [documentId]);

  const liveSelection = useMemo(() => selection.filter((s) => s in doc.text), [selection, doc]);

  /** Choosing paragraphs from the gutter is a deliberate statement of scope,
   *  so it ends whatever was selected inside a block. */
  const selectRows = useCallback(
    (ids: ParagraphId[]) => {
      setTextSelection(null);
      setClearText((n) => n + 1);
      onSelectionChange(ids);
    },
    [onSelectionChange],
  );

  // What the tray's Review button would be about. A selection with a gap in
  // it is two passages, so it becomes two calls, started together.
  const scope = useMemo(
    () => selectionScope(textSelection?.id ?? null, liveSelection, doc),
    [textSelection, liveSelection, doc],
  );
  const reviewing =
    advisor.runs.find(
      (r) => r.kind === "review" && r.trigger === "global" && r.status === "running",
    ) ?? null;

  const reassessing = useMemo(
    () =>
      new Set(
        advisor.runs
          .filter((r) => r.trigger === "reassess" && r.status === "running" && r.slug)
          .map((r) => r.slug!),
      ),
    [advisor.runs],
  );

  // A comment we were waiting on came back addressed: flash Success, then let
  // it go where resolved comments go.
  useEffect(() => {
    for (const comment of advisor.state.comments) {
      if (!awaiting.current.has(comment.slug)) continue;
      if (comment.state === "resolved" && comment.dismiss_reason === "addressed") {
        awaiting.current.delete(comment.slug);
        setFlashing((m) => new Map(m).set(comment.slug, comment));
        window.setTimeout(() => {
          setFlashing((m) => {
            const next = new Map(m);
            next.delete(comment.slug);
            return next;
          });
        }, SUCCESS_MS);
      } else if (comment.state !== "live") {
        awaiting.current.delete(comment.slug);
      }
    }
  }, [advisor.state.comments]);

  const liveComments = useMemo(
    () => openComments(advisor.state.comments),
    [advisor.state.comments],
  );
  const shownComments = useMemo(
    () => [...liveComments, ...flashing.values()],
    [liveComments, flashing],
  );
  const trays = useMemo(() => trayIndex(shownComments, doc.order), [shownComments, doc.order]);
  const trayCounts = useMemo(() => {
    const out = new Map<ParagraphId, number>();
    for (const [id, list] of trays) out.set(id, list.length);
    return out;
  }, [trays]);
  // Comments with no paragraph to sit under, at the top with the card that
  // says where the author stands. Nothing is promoted above them and nothing
  // is ranked: direction comes from the evaluation's goal, not from an
  // ordering three independent calls could not agree on (D28).
  const docWide = useMemo(() => docScoped(shownComments), [shownComments]);

  const say = useCallback((message: string, ms = 3000) => {
    setShared(message);
    window.setTimeout(() => setShared(null), ms);
  }, []);

  // Stable, because History watches it: an arrow rebuilt on every render
  // would re-run the effect that loads a version and never settle.
  const notify = useCallback((message: string) => say(message, 5000), [say]);

  /** Start one or more calls, and say so if none of them could be started.
   *
   *  Unsent edits go first, so the advisor reads the text as it is on screen
   *  rather than as the server last saw it. */
  const start = useCallback(
    async (bodies: Record<string, unknown>[]) => {
      setAsking(true);
      try {
        await outbox.flush();
        const results = await Promise.allSettled(
          bodies.map((one) => api.post(`/api/documents/${documentId}/runs`, one)),
        );
        // One of two calls failing is not a failure the author needs to see:
        // whichever started will still arrive. All of them failing is.
        if (results.every((r) => r.status === "rejected")) {
          throw (results[0] as PromiseRejectedResult).reason;
        }
        await advisor.refreshRuns();
      } catch (e) {
        say(
          e instanceof ApiError ? e.message : "Could not reach the advisor. Check your connection.",
          5000,
        );
      } finally {
        setAsking(false);
      }
    },
    [documentId, advisor, outbox, say],
  );

  /** The dock's button: the whole text, always.
   *
   *  Two calls rather than one. The review says what to do next and the
   *  evaluation says where the author stands; neither waits for the other, and
   *  whichever commits first appears first (Phase 4.5). */
  const reviewEverything = useCallback(
    () => start([{ kind: "review", trigger: "global" }, { kind: "evaluation" }]),
    [start],
  );

  /** Only the models that could do a review on this server. Settings shows
   *  the ones with no key, disabled, and explains why; the dock is a quick
   *  action and has no business explaining a deployment. */
  const reviewModels = useMemo(
    () => me.available_models.filter((m) => m.tiers.includes("review") && m.configured),
    [me.available_models],
  );

  /** Choosing from the menu changes the account's model and then reviews with
   *  it, so the two places this can be set never disagree. If the change does
   *  not land, nothing is reviewed: a run against a model the author did not
   *  choose is worse than no run. */
  const chooseModel = useCallback(
    async (id: string | null) => {
      setModelMenu(false);
      try {
        const updated = await api.patch<Me>("/api/me", { model_overrides: { review: id } });
        setMe(updated);
        rememberMe(updated);
      } catch (e) {
        say(e instanceof ApiError ? e.message : "Could not change the model.", 4000);
        return;
      }
      void reviewEverything();
    },
    [say, reviewEverything],
  );

  const reviewButton = useRef<HTMLButtonElement | null>(null);
  const holdReview = useLongPress(() => setModelMenu(true));

  /** Shutting the menu gives the keyboard back to the button that opened it.
   *  Without this, Escape leaves focus on nothing and the next Tab starts
   *  again from the top of the page. */
  const closeModelMenu = useCallback(() => {
    setModelMenu(false);
    reviewButton.current?.focus();
  }, []);

  /** The tray's button: the passage the author has chosen, and only that. */
  const reviewSelection = useCallback(async () => {
    if (scope.length === 0) return;
    await start(scope.map((anchors) => ({ kind: "review", trigger: "selection", anchors })));
    selectRows([]);
  }, [scope, start, selectRows]);

  const dismiss = useCallback(
    async (slug: string) => {
      try {
        await api.post(`/api/documents/${documentId}/comments/${slug}/dismiss`, {});
        await advisor.refresh();
        if (undo) window.clearTimeout(undo.timer);
        const timer = window.setTimeout(() => setUndo(null), 6000);
        setUndo({ slug, timer });
      } catch {
        say("Could not dismiss that.");
      }
    },
    [documentId, advisor, undo, say],
  );

  const undoDismiss = useCallback(async () => {
    if (!undo) return;
    window.clearTimeout(undo.timer);
    const slug = undo.slug;
    setUndo(null);
    try {
      await api.post(`/api/documents/${documentId}/comments/${slug}/undismiss`, {});
      await advisor.refresh();
    } catch {
      // it stays dismissed; the archive still has it
    }
  }, [documentId, advisor, undo]);

  const dismissNote = useCallback(
    async (slug: string) => {
      try {
        await api.post(`/api/documents/${documentId}/notes/${slug}/dismiss`, {});
        await advisor.refresh();
      } catch {
        say("Could not put that away.");
      }
    },
    [documentId, advisor, say],
  );

  const dismissEvaluation = useCallback(async () => {
    try {
      await api.post(`/api/documents/${documentId}/evaluation/dismiss`, {});
      await advisor.refresh();
    } catch {
      say("Could not put that away.");
    }
  }, [documentId, advisor, say]);

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        say("Copied");
      } catch {
        say("Could not copy on this device");
      }
    },
    [say],
  );

  const reassess = useCallback(
    async (slug: string) => {
      // Send the edit first, so the server reads what is on screen.
      await outbox.flush();
      try {
        awaiting.current.add(slug);
        await api.post(`/api/documents/${documentId}/comments/${slug}/reassess`, {});
        await advisor.refreshRuns();
      } catch (e) {
        awaiting.current.delete(slug);
        say(e instanceof ApiError ? e.message : "Could not reach the advisor.", 4000);
      }
    },
    [documentId, advisor, outbox, say],
  );

  const setSearch = useCallback(
    async (on: boolean) => {
      try {
        const updated = await api.patch<ServerDocument>(`/api/documents/${documentId}`, {
          search_enabled: on,
        });
        setSearchEnabled(updated.search_enabled);
        setNoticeShown(updated.search_notice_shown_at !== null);
        if (!on) say("Web search off.", 2500);
      } catch {
        say("Could not change that.");
      }
    },
    [documentId, say],
  );

  /** Turning search on the first time says what leaves the device before it
   *  does (design §9.2). Not a modal: the sheet sits above the dock and the
   *  author can keep typing past it. Afterwards the globe is a plain toggle. */
  const toggleSearch = useCallback(
    (on: boolean) => {
      if (on && noticeShown === false) return void setSheet(true);
      void setSearch(on);
    },
    [noticeShown, setSearch],
  );

  const acceptSearch = useCallback(() => {
    setSheet(false);
    void setSearch(true);
  }, [setSearch]);

  const renderCard = useCallback(
    (comment: Comment) => (
      <CommentCard
        key={comment.slug}
        comment={comment}
        notes={advisor.state.notes}
        searchEnabled={searchEnabled}
        reassessing={reassessing.has(comment.slug)}
        success={flashing.has(comment.slug)}
        onDismiss={() => void dismiss(comment.slug)}
        onReassess={() => void reassess(comment.slug)}
        onCopy={(text) => void copy(text)}
        onEnableSearch={() => toggleSearch(true)}
      />
    ),
    [
      advisor.state.notes,
      searchEnabled,
      reassessing,
      flashing,
      dismiss,
      reassess,
      copy,
      toggleSearch,
    ],
  );

  // Cache only when nothing is owed to the server, so what is stored is the
  // server's version and the queue alone accounts for the difference.
  // Caching the merged view would count this device's work twice on reopen.
  useEffect(() => {
    if (outbox.queued > 0) return;
    const timer = window.setTimeout(() => {
      void saveDocument(documentId, doc, revRef.current ?? 0);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [doc, status, documentId, outbox, revRef]);

  const words = useMemo(
    () => paragraphsOf(doc).join(" ").split(/\s+/).filter(Boolean).length,
    [doc],
  );

  // Two things wear the same button. A selection inside a paragraph copies
  // exactly the characters it covers; the editor cannot select across
  // paragraphs (plan D3), so several at once come from the gutter instead and
  // are joined back into markdown.
  const copySelection = useCallback(async () => {
    const [text, said] = textSelection
      ? [textSelection.text, "Copied"]
      : [
          joinMarkdown(liveSelection.map((id) => doc.text[id] ?? "")),
          `Copied ${liveSelection.length} paragraph${liveSelection.length === 1 ? "" : "s"}`,
        ];
    try {
      await navigator.clipboard.writeText(text);
      say(said);
    } catch {
      say("Could not copy on this device");
    }
  }, [doc, liveSelection, textSelection, say]);

  /** Stop everything in flight, including what the advisor started by
   *  itself: if the author is asking for it to stop, they mean all of it. */
  const cancelAll = useCallback(async () => {
    const running = advisor.runs.filter((r) => r.status === "running");
    await Promise.allSettled(
      running.map((r) => api.post(`/api/runs/${r.id}/cancel`, {})),
    );
    await advisor.refreshRuns();
  }, [advisor]);

  const share = useCallback(async () => {
    const markdown = joinMarkdown(paragraphsOf(doc));
    const name = `${doc.title.trim() || "Untitled"}.md`;
    const file = new File([markdown], name, { type: "text/markdown" });
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    try {
      if (nav.share && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: doc.title || "Untitled" });
        return;
      }
    } catch {
      // fall through to copying
    }
    try {
      await navigator.clipboard.writeText(markdown);
      say("Markdown copied");
    } catch {
      say("Could not share on this device");
    }
  }, [doc, say]);

  /** Where a note points. The body may not be mounted, so the paragraph is
   *  left for it to find rather than scrolled to from here. */
  const onGoToParagraph = useCallback(
    (id: ParagraphId) => {
      pendingScroll.current = id;
      navigate(base);
    },
    [navigate, base],
  );

  const onToggleTray = useCallback(
    (id: ParagraphId) => setOpenTray((current) => (current === id ? null : id)),
    [],
  );

  const onDismissNote = useCallback((slug: string) => void dismissNote(slug), [dismissNote]);
  const onDismissEvaluation = useCallback(() => void dismissEvaluation(), [dismissEvaluation]);

  const context: DocumentContext = useMemo(
    () => ({
      documentId,
      doc,
      store,
      advisor,
      advisorBump,
      me,
      liveSelection,
      onSelectionChange: selectRows,
      onTextSelection: setTextSelection,
      clearText,
      find,
      trays,
      trayCounts,
      docWide,
      openTray,
      onToggleTray,
      renderCard,
      onDismissEvaluation,
      onDismissNote,
      onGoToParagraph,
      onRestored,
      onNotice: notify,
      pendingScroll,
    }),
    [
      documentId,
      doc,
      store,
      advisor,
      advisorBump,
      me,
      liveSelection,
      selectRows,
      clearText,
      find,
      trays,
      trayCounts,
      docWide,
      openTray,
      onToggleTray,
      renderCard,
      onDismissEvaluation,
      onDismissNote,
      onGoToParagraph,
      onRestored,
      notify,
    ],
  );

  return (
    <div className="screen">
      <TopBar
        title={
          <input
            className="title-input"
            value={doc.title}
            placeholder="Untitled"
            // An input is as wide as its `size`, not as its value, so without
            // this a long title is cut off long before the bar runs out.
            size={Math.min(52, Math.max(10, doc.title.length + 2))}
            onChange={(e) => store.dispatch({ t: "set_title", title: e.target.value })}
            aria-label="Document title"
          />
        }
      />

      <main className="body">
        {/* Computed here and not inside the routes, so the disc never
            remounts on the way between them. One step back at a time: the log
            returns to the research it is part of, a panel returns to the text,
            and the text returns to the library. */}
        <BackDisc
          to={inLog ? `${base}/research` : inResearch || inHistory ? base : "/"}
          label={
            inLog
              ? "Back to what it understands"
              : inResearch || inHistory
                ? "Back to the document"
                : "Back to the library"
          }
        />

        <Outlet context={context} />
      </main>

      <div className="dock" style={{ bottom: `calc(var(--dock-gap) + ${keyboardInset}px)` }}>
        {sheet && (
          <div className="sheet" role="note">
            <h3>Before it looks things up</h3>
            <p>
              With web search on, fragments of this document become queries sent to a search
              provider. It is off by default, per document, and you can turn it off again at any
              time.
            </p>
            <div className="sheet-actions">
              <button type="button" className="text-button quiet" onClick={() => setSheet(false)}>
                Not now
              </button>
              <button type="button" className="text-button" onClick={acceptSearch}>
                Turn it on
              </button>
            </div>
          </div>
        )}

        {modelMenu && (
          <ModelMenu
            models={reviewModels}
            defaultId={me.model_defaults.review}
            current={me.model_overrides.review}
            onChoose={(id) => void chooseModel(id)}
            onClose={closeModelMenu}
          />
        )}

        {undo && (
          <div className="toast">
            <span>Dismissed</span>
            <button type="button" className="text-button" onClick={() => void undoDismiss()}>
              Undo
            </button>
          </div>
        )}

        {/* Either kind of selection raises it. Until now only the gutter did,
            which left a mid-paragraph selection with a strip that named an
            action and no button to press. */}
        {(liveSelection.length > 0 || textSelection !== null) && (
          <div className="selrow">
            <span className="bar-hint">
              {textSelection
                ? `${textSelection.text.length} character${
                    textSelection.text.length === 1 ? "" : "s"
                  }`
                : `${liveSelection.length} paragraph${liveSelection.length === 1 ? "" : "s"}`}
            </span>
            {/* The only review that is about a passage. The one in the dock is
                always the whole text. */}
            <IconButton
              icon="brain"
              label={selectionLabel(scope)}
              primary
              size={24}
              busy={asking}
              disabled={asking || status === "offline" || scope.length === 0}
              onClick={() => void reviewSelection()}
            />
            <IconButton
              icon="copy"
              label={textSelection ? "Copy the selected text" : "Copy these paragraphs"}
              size={22}
              onClick={() => void copySelection()}
            />
            <IconButton
              icon="trash"
              label="Clear the selection"
              size={22}
              onClick={() => selectRows([])}
            />
          </div>
        )}

        {finding && (
          <div className="findbar">
            <input
              type="search"
              className="search"
              autoFocus
              placeholder="Find in document"
              value={find.query}
              onChange={(e) => find.setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.shiftKey ? find.previous : find.next)();
                if (e.key === "Escape") setFinding(false);
              }}
              aria-label="Find in document"
            />
            <span className="bar-hint">
              {find.query.trim() === ""
                ? ""
                : find.matches.length === 0
                  ? "none"
                  : `${find.index + 1} of ${find.matches.length}`}
            </span>
            <IconButton
              icon="back"
              label="Previous match"
              size={22}
              onClick={find.previous}
              disabled={find.matches.length === 0}
            />
            <span className="next-match">
              <IconButton
                icon="back"
                label="Next match"
                size={22}
                onClick={find.next}
                disabled={find.matches.length === 0}
              />
            </span>
            <IconButton icon="close" label="Done finding" size={22} onClick={() => setFinding(false)} />
          </div>
        )}

        <div className="pill">
          <div className="strip" aria-live="polite">
            <span className="strip-text">
              {shared ??
                notice ??
                advisorText(advisor.active, advisor.runs) ??
                /* A selection made inside a paragraph raises the tray like any
                   other, so its Review button is reachable; the strip says
                   what pressing it would be about. */
                (scope.length > 0 ? selectionLabel(scope) : null) ??
                /* Persistent while it applies, and never in the way: the
                   ceiling suspends the quiet reading and says so, and asking
                   and reviewing carry on (design §12.3). */
                (me.budget.over
                  ? "Past your monthly ceiling — reading in the background is paused. " +
                    "The button still works."
                  : null) ??
                stripText(status, live, words, outbox.queued)}
            </span>
            {advisor.active && (
              <button
                type="button"
                className="text-button"
                onClick={() => void cancelAll()}
              >
                Cancel
              </button>
            )}
          </div>

          <div className="pill-row spread">
            {/* Always the whole text (Phase 4.5), and always two calls: the
                review and the evaluation, side by side. */}
            {/* Held down or right-clicked, it offers the model instead of
                starting a review. Only this one: the selection's Review takes
                whatever model is in use, and two buttons offering two
                different things would invite the question of which is which. */}
            <IconButton
              icon="brain"
              label={reviewing ? "Reading the whole thing" : "Review the whole thing"}
              primary
              /* Busy for what this button would do, not for everything in
                 flight: a review of a passage running elsewhere is the strip's
                 news and does not stop this one. */
              busy={reviewing !== null}
              disabled={asking || status === "offline" || reviewing !== null}
              onClick={() => {
                if (holdReview.swallowed()) return;
                void reviewEverything();
              }}
              hold={{
                ...holdReview.handlers,
                ref: reviewButton,
                "aria-haspopup": "menu",
                "aria-expanded": modelMenu,
              }}
            />
            <IconButton
              icon="book"
              label="What it understands"
              active={inResearch}
              onClick={() => navigate(inResearch ? base : `${base}/research`)}
            />
            <IconButton
              icon="clock"
              label="Earlier versions"
              active={inHistory}
              onClick={() => navigate(inHistory ? base : `${base}/history`)}
            />
            <IconButton
              icon="find"
              label="Find in document"
              active={finding}
              onClick={() => setFinding((f) => !f)}
            />
            <IconButton
              icon="globe"
              label={
                !canSearch
                  ? "This model cannot show you the pages it reads, so search is off"
                  : searchEnabled
                    ? "Web search is on"
                    : "Let it check the web"
              }
              active={searchEnabled && canSearch}
              disabled={!canSearch}
              onClick={() => toggleSearch(!searchEnabled)}
            />
            <IconButton icon="share" label="Share as markdown" onClick={() => void share()} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** What the tray's Review button would be about, which is the only way its
 *  scope is visible before it runs. */
function selectionLabel(scope: ParagraphId[][]): string {
  if (scope.length > 1) return `Review these ${scope.length} passages`;
  if (scope.length === 1) {
    const n = scope[0]!.length;
    return n === 1 ? "Review this paragraph" : `Review these ${n} paragraphs`;
  }
  return "Review";
}

/** What a run is doing, from the call and the surface that asked for it: the
 *  same call reads very differently depending on what it was pointed at. */
function working(run: Run): string {
  if (run.kind === "evaluation") return "Working out where you stand";
  if (run.kind === "research") return "Looking things up";
  if (run.trigger === "selection") return "Reading the passage";
  if (run.trigger === "reassess") return "Reassessing";
  return "Reading the document";
}

function done(run: Run): string {
  if (run.kind === "evaluation") return "Card updated";
  if (run.kind === "research") return "Researched";
  if (run.trigger === "reassess") return "Reassessed";
  return "Reviewed";
}

/** While the advisor is working the strip says what it is doing, derived from
 *  the stream rather than from anything the model claims about itself
 *  (design §12.1). When it is idle, the last run and what it cost. */
function advisorText(active: Run | null, runs: Run[]): string | null {
  if (active) {
    // Several calls can be in flight at once — the button starts two — and the
    // strip has to say so, or cancelling one would look like cancelling all.
    const alongside = runs.filter((r) => r.status === "running").length;
    const spent = runs
      .filter((r) => r.status === "running")
      .reduce((total, r) => total + r.cost_usd, 0);
    const cost = spent > 0 ? ` · $${spent.toFixed(3)}` : "";
    if (alongside > 1) return `Reading · ${alongside} at once${cost}`;
    return `${working(active)}${cost}`;
  }
  // What the advisor did unasked is not news; the idle line prefers the last
  // thing the author pressed something for.
  const finished = runs.filter((r) => r.status !== "running");
  const last = finished.find((r) => r.trigger !== "background") ?? finished[0];
  if (!last) return null;
  if (last.status === "failed") {
    return `${done(last)} failed — ${last.error?.message ?? "tap the log for detail"}`;
  }
  if (last.status === "cancelled") {
    return `Cancelled · $${last.cost_usd.toFixed(3)}`;
  }
  return `${done(last)} ${when(last.started_at)} · $${last.cost_usd.toFixed(3)}`;
}

function stripText(status: SyncStatus, live: boolean, words: number, queued: number): string {
  const count = `${words} word${words === 1 ? "" : "s"}`;
  switch (status) {
    case "offline":
      return `Offline — ${queued} unsaved change${queued === 1 ? "" : "s"} kept on this device`;
    case "syncing":
      return `Saving… · ${count}`;
    case "conflict":
      return `Reloaded from the server · ${count}`;
    default:
      return `${live ? "Saved" : "Saved · reconnecting"} · ${count}`;
  }
}
